<script setup lang="ts">
import AuthenticationService from '@/core/shared/authentication/authentication.service';
import MeService from '@/core/shared/me/me.service';
import { ErrorService } from '@/core/shared/errors/error.service';

function reloadPage(): void {
  window.location.reload();
}

// Escape hatch for failures a reload can never fix (e.g. a misclassified
// permanent error): logout only clears local state, so it needs no API data.
async function signOut(): Promise<void> {
  // Always hand off to UI2 logout even if local logout throws — this is a
  // last-resort escape hatch, so the redirect must not depend on it. UI2's
  // logout route re-clears the session, so a failed local logout is recoverable.
  try {
    await new AuthenticationService().logout();
  } catch (error) {
    ErrorService.captureException(error);
  }
  new MeService().redirectToUI2('/employer/auth/logout');
}
</script>

<template>
  <div class="flex min-h-screen w-full items-center justify-center px-4">
    <div class="flex flex-col items-center sm:flex-row">
      <div class="mb-6 flex w-full max-w-[364px] flex-col sm:mb-0 sm:mr-6">
        <span class="font-serif text-2xl font-650">We couldn't load your dashboard</span>

        <span class="mt-6 text-base">
          Please try again — reloading usually fixes it. If it keeps happening, sign out and back
          in.
        </span>

        <div class="mt-6 flex items-center gap-4">
          <button
            type="button"
            data-test="bootstrap-error-reload"
            class="flex h-10 w-40 cursor-pointer items-center justify-center rounded-md border-2 border-shade-880 transition-colors hover:bg-tint-40"
            @click="reloadPage"
          >
            <span class="font-sans text-sm font-semibold text-shade-880">Reload</span>
          </button>
          <button
            type="button"
            data-test="bootstrap-error-sign-out"
            class="cursor-pointer font-sans text-sm font-semibold text-shade-880 underline underline-offset-2 hover:text-shade-800"
            @click="signOut"
          >
            Sign out
          </button>
        </div>
      </div>

      <div class="flex">
        <img
          src="@/assets/png/worker-wearing-safety-vest.png"
          alt="A worker wearing an orange safety vest, hard hat and safety goggles"
          class="w-[170px] object-contain"
        />
      </div>
    </div>
  </div>
</template>
